import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import {
  GoogleMap,
  Marker,
  OverlayView,
  OverlayViewF,
  OVERLAY_MOUSE_TARGET,
  DirectionsService,
  DirectionsRenderer,
  useJsApiLoader,
} from '@react-google-maps/api';
import io from 'socket.io-client';
import { GOOGLE_MAPS_TRAVEL_MODE } from '../../../constants/constants';
import { CarIcon } from '../../../assets/svgs/UberCarIcon';
import styles from './index.module.css';

const DEFAULT_MAP_HEIGHT = '380px';

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
};

const computeBearing = (from, to) => {
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

const toRad = (deg) => (deg * Math.PI) / 180;

const haversineMeters = (a, b) => {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
};

const BACKTRACK_TOLERANCE_SEGMENTS = 2;

const OFF_ROUTE_METERS = 75;

/**
 * Projects carPos onto each segment of fullPath at/after searchFromIdx (nearest
 * point on segment, not just nearest vertex). Returns the matched segment index,
 * how far (meters) the car actually is from the route, and [carPos, ...remainingPathPoints]
 * — always starts at the car with nothing behind it.
 */
const matchCarToPath = (fullPath, carPos, searchFromIdx = 0) => {
  if (!fullPath || fullPath.length < 2 || !carPos) return null;

  const startIdx = Math.max(0, searchFromIdx - BACKTRACK_TOLERANCE_SEGMENTS);
  let bestSegIdx = startIdx;
  let minDist = Infinity;
  let bestPoint = null;

  for (let i = startIdx; i < fullPath.length - 1; i++) {
    const ax = fullPath[i].lng, ay = fullPath[i].lat;
    const bx = fullPath[i + 1].lng, by = fullPath[i + 1].lat;
    const px = carPos.lng, py = carPos.lat;

    const dx = bx - ax, dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = 0;
    if (lenSq > 0) {
      t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
    }
    const cx = ax + t * dx, cy = ay + t * dy;
    const dist = (px - cx) ** 2 + (py - cy) ** 2;

    if (dist < minDist) {
      minDist = dist;
      bestSegIdx = i;
      bestPoint = { lat: cy, lng: cx };
    }
  }

  const distanceMeters = bestPoint ? haversineMeters(carPos, bestPoint) : Infinity;
  const ahead = fullPath.slice(bestSegIdx + 1);
  const path = ahead.length === 0
    ? [{ lat: carPos.lat, lng: carPos.lng }]
    : [{ lat: carPos.lat, lng: carPos.lng }, ...ahead];

  return { path, matchedIdx: bestSegIdx, distanceMeters };
};

const ConnectingBadge = () => (
  <div className={styles.connectingBadge}>
    <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
    <span>Waiting for driver location…</span>
  </div>
);

export const UberLiveMapInline = ({
  rideId,
  pickupAddress,
  destinationAddress,
  lastLocation,
  apiUrl,
  isCompleted,
  height,
}) => {
  const [driverState, setDriverState] = useState(() => {
    if (lastLocation?.latitude != null && lastLocation?.longitude != null) {
      return {
        lat: lastLocation.latitude,
        lng: lastLocation.longitude,
        bearing: lastLocation.bearing || 0,
      };
    }
    return null;
  });

  const [directions, setDirections] = useState(null);
  const [directionsFetched, setDirectionsFetched] = useState(false);
  const [markerCoords, setMarkerCoords] = useState({ pickup: null, destination: null });

  const mapRef = useRef(null);
  const socketRef = useRef(null);
  const driverStateRef = useRef(driverState);
  const hasLiveLocationRef = useRef(false);
  const hasInitialZoomedRef = useRef(false);

  const fullRoutePathRef = useRef(null);
  const routeComputedRef = useRef(false);
  const progressIdxRef = useRef(0);
  const recomputeInFlightRef = useRef(false);
  const lastRecomputeAtRef = useRef(0);

  const livePolylineRef = useRef(null);
  const pendingPathRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (!rideId || isCompleted) return;

    const baseUrl = apiUrl || window.location.origin;
    const socket = io(`${baseUrl}/uber-location`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: Infinity,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('subscribe_ride', { rideId });
    });

    socket.on('location_update', (data) => {
      if (data.rideId !== rideId) return;
      const lat = data.latitude;
      const lng = data.longitude;
      if (lat == null || lng == null) return;

      if (data.isInitial) {
        if (!hasLiveLocationRef.current) {
          setDriverState({ lat, lng, bearing: data.bearing ?? 0 });
        }
        return;
      }

      let bearing = data.bearing ?? null;
      if (bearing == null && driverStateRef.current) {
        bearing = computeBearing(driverStateRef.current, { lat, lng });
      }
      hasLiveLocationRef.current = true;
      setDriverState({ lat, lng, bearing: bearing ?? 0 });
    });

    return () => {
      socket.emit('unsubscribe_ride', { rideId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [rideId, apiUrl, isCompleted]);

  useEffect(() => {
    if (isCompleted || !pickupAddress || !destinationAddress || !isLoaded) return;
    if (routeComputedRef.current) return;
    routeComputedRef.current = true;

    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: pickupAddress,
        destination: destinationAddress,
        travelMode:
          window.google.maps.TravelMode[GOOGLE_MAPS_TRAVEL_MODE] ||
          window.google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (status !== 'OK') {
          console.warn(`[UberLiveMapInline] Route computation failed: ${status}`);
          return;
        }
        const route = response.routes[0];
        const path = route.overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
        fullRoutePathRef.current = path;
        progressIdxRef.current = 0;

        const leg = route.legs[0];
        if (leg) {
          setMarkerCoords((prev) => ({
            ...prev,
            destination: { lat: leg.end_location.lat(), lng: leg.end_location.lng() },
          }));
        }
        if (driverStateRef.current) {
          const match = matchCarToPath(path, driverStateRef.current, 0);
          if (match) {
            progressIdxRef.current = match.matchedIdx;
            applyTrimmedPath(match.path);
          }
        }
      }
    );
  }, [isCompleted, pickupAddress, destinationAddress, isLoaded]);

  const applyTrimmedPath = useCallback((path) => {
    pendingPathRef.current = path;
    if (livePolylineRef.current) {
      livePolylineRef.current.setPath(path || []);
    }
  }, []);

  const recomputeRouteFromCar = useCallback(
    (carPos) => {
      if (!isLoaded || !window.google || !destinationAddress) return;
      if (recomputeInFlightRef.current) return;
      const now = Date.now();
      if (now - lastRecomputeAtRef.current < 15000) return;

      recomputeInFlightRef.current = true;
      lastRecomputeAtRef.current = now;

      const service = new window.google.maps.DirectionsService();
      service.route(
        {
          origin: carPos,
          destination: destinationAddress,
          travelMode:
            window.google.maps.TravelMode[GOOGLE_MAPS_TRAVEL_MODE] ||
            window.google.maps.TravelMode.DRIVING,
        },
        (response, status) => {
          recomputeInFlightRef.current = false;
          if (status !== 'OK') {
            console.warn(`[UberLiveMapInline] Route re-computation failed: ${status}`);
            return;
          }
          const path = response.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
          fullRoutePathRef.current = path;
          progressIdxRef.current = 0;
          const match = matchCarToPath(path, carPos, 0);
          if (match) {
            progressIdxRef.current = match.matchedIdx;
            applyTrimmedPath(match.path);
          }
        }
      );
    },
    [isLoaded, destinationAddress, applyTrimmedPath]
  );

  useEffect(() => {
    if (isCompleted || !driverState) return;
    if (!fullRoutePathRef.current) return;

    const match = matchCarToPath(fullRoutePathRef.current, driverState, progressIdxRef.current);
    if (!match) return;

    if (match.distanceMeters > OFF_ROUTE_METERS) {
      recomputeRouteFromCar({ lat: driverState.lat, lng: driverState.lng });
      return;
    }

    progressIdxRef.current = match.matchedIdx;
    applyTrimmedPath(match.path);
  }, [driverState, isCompleted, applyTrimmedPath, recomputeRouteFromCar]);

  useEffect(() => {
    driverStateRef.current = driverState;
    if (driverState && mapRef.current) {
      mapRef.current.panTo({ lat: driverState.lat, lng: driverState.lng });
      if (!hasInitialZoomedRef.current) {
        if (mapRef.current.getZoom() < 14) {
          mapRef.current.setZoom(15);
        }
        hasInitialZoomedRef.current = true;
      }
    }
  }, [driverState]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;

    if (!isCompleted) {
      livePolylineRef.current = new window.google.maps.Polyline({
        map,
        path: [],
        strokeColor: '#315E45',
        strokeWeight: 4,
        strokeOpacity: 1,
      });
      if (pendingPathRef.current) {
        livePolylineRef.current.setPath(pendingPathRef.current);
      }
    }
  }, [isCompleted]);

  useEffect(() => {
    return () => {
      if (livePolylineRef.current) {
        livePolylineRef.current.setMap(null);
        livePolylineRef.current = null;
      }
    };
  }, []);

  const handleDirectionsCallback = useCallback(
    (response) => {
      if (response?.status === 'OK' && !directionsFetched) {
        setDirections(response);
        setDirectionsFetched(true);
        const leg = response.routes?.[0]?.legs?.[0];
        if (leg) {
          setMarkerCoords({
            pickup: { lat: leg.start_location.lat(), lng: leg.start_location.lng() },
            destination: { lat: leg.end_location.lat(), lng: leg.end_location.lng() },
          });
        }
        if (mapRef.current && response.routes?.[0]?.bounds) {
          mapRef.current.fitBounds(response.routes[0].bounds, 40);
        }
      } else if (response?.status && response.status !== 'OK') {
        console.warn(`[UberLiveMapInline] Static directions request failed: ${response.status}`);
      }
    },
    [directionsFetched]
  );

  const mapContainerStyle = useMemo(
    () => ({ width: '100%', height: height || DEFAULT_MAP_HEIGHT }),
    [height]
  );

  if (!isLoaded) {
    return (
      <div className={styles.mapLoadingContainer}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
      </div>
    );
  }

  const hasRoute = pickupAddress && destinationAddress;

  const markerSvg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="11" fill="#1C452B"/>
      <circle cx="12" cy="12" r="5" fill="#E1F4D3"/>
    </svg>`
  );
  const circleMarkerIcon = {
    url: `data:image/svg+xml;charset=UTF-8,${markerSvg}`,
    scaledSize: new window.google.maps.Size(24, 24),
    anchor: new window.google.maps.Point(12, 12),
  };

  return (
    <div className={styles.mapWrapper}>
      {!isCompleted && !driverState && <ConnectingBadge />}

      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={13}
        options={mapOptions}
        onLoad={onMapLoad}
      >
      
        {hasRoute && isCompleted && !directionsFetched && (
          <DirectionsService
            options={{
              origin: pickupAddress,
              destination: destinationAddress,
              travelMode: GOOGLE_MAPS_TRAVEL_MODE,
            }}
            callback={handleDirectionsCallback}
          />
        )}
        {directions && isCompleted && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              preserveViewport: true,
              polylineOptions: {
                strokeColor: '#1a73e8',
                strokeWeight: 4,
                strokeOpacity: 1,
              },
            }}
          />
        )}

        {/* Pickup marker — completed rides only */}
        {markerCoords.pickup && isCompleted && (
          <Marker position={markerCoords.pickup} icon={circleMarkerIcon} zIndex={1} />
        )}
        {markerCoords.pickup && isCompleted && (
          <OverlayView
            position={markerCoords.pickup}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div className={styles.overlayLabelRoot}>
              <div className={styles.labelCard}>
                <div className={styles.labelTitle}>Pickup</div>
                <div className={styles.labelAddress}>{pickupAddress}</div>
              </div>
            </div>
          </OverlayView>
        )}

        {markerCoords.destination && (
          <Marker position={markerCoords.destination} icon={circleMarkerIcon} zIndex={1} />
        )}
        {markerCoords.destination && (
          <OverlayView
            position={markerCoords.destination}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
          >
            <div className={styles.overlayLabelRoot}>
              <div className={styles.labelCard}>
                <div className={styles.labelTitle}>Destination</div>
                <div className={styles.labelAddress}>{destinationAddress}</div>
              </div>
            </div>
          </OverlayView>
        )}

        {/* Car icon — in-progress only */}
        {driverState && !isCompleted && (
          <OverlayViewF
            position={{ lat: driverState.lat, lng: driverState.lng }}
            mapPaneName={OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => ({ x: -w / 2, y: -h / 2 })}
          >
            <div
              className={styles.uberMapCarWrap}
              style={{ transform: `rotate(${driverState.bearing}deg)` }}
              title="Driver"
            >
              <div className={styles.uberMapCarMap}>
                <CarIcon width="100%" height="100%" className={styles.uberMapCarSvg} aria-hidden />
              </div>
            </div>
          </OverlayViewF>
        )}
      </GoogleMap>
    </div>
  );
};
